<script setup lang="ts">
import RoomSvg from '@/components/svgs/RoomSvg.vue'
import { CULTURE, getRoomName } from '@/composables/helpers'
import type { IRoom, IRoomDto } from '@/composables/interfaces/appInterfaces'
import t from '@/i18n/translations'
import { rooms } from '@/store/accosbar.store'

const props = defineProps<{
  roomItem: IRoomDto | undefined
  addRoomClass: string | null
}>()

const availableAges = Array.from({ length: 18 }, (_, i) => i)

function addRoom(): void {
  rooms.value = rooms.value ? [...rooms.value, { adults: 2, children: 0, childrenAges: [] }] : [{ adults: 2, children: 0, childrenAges: [] }]
}

function removeRoom(index: number): void {
  rooms.value = rooms.value.filter((_: any, i: number) => i !== index)
}

function updateAdults(roomIndex: number, value: number): void {
  rooms.value = rooms.value.map((room: any, index: number) => {
    return roomIndex === index
      ? {
          ...room,
          adults: Math.max(1, room.adults + value)
        }
      : room
  })
}

function updateChildren(roomIndex: number, value: number): void {
  rooms.value = rooms.value.map((room: IRoom, index: number) => {
    if (roomIndex !== index) return room

    const originalAges = Array.isArray(room.childrenAges) ? room.childrenAges : []
    let updatedChildrenAges = [...originalAges]

    if (value > 0) {
      updatedChildrenAges.push(0)
    } else if (value < 0 && updatedChildrenAges.length > 0) {
      updatedChildrenAges.pop()
    }

    const updatedChildren = Math.max(0, room.children + value)

    return {
      ...room,
      children: updatedChildren,
      childrenAges: updatedChildrenAges
    }
  })
}
</script>
<template>
  <div class="occupancy-picker my-4 flex flex-col gap-4">
    <div v-for="(room, index) in rooms" :key="index" class="pui-card relative grid w-full rounded p-3 first:border-0 md:p-5">
      <div class="mb-4 flex items-center">
        <RoomSvg />
        <div class="ml-2 text-sm font-semibold md:text-xl">
          {{ index === 0 && props.roomItem ? getRoomName(props.roomItem, CULTURE) : `${t(`${CULTURE}.occupancy.rooms`)} ${index + 1}` }}
        </div>
        <button @click="removeRoom(index)" :disabled="index === 0 || rooms.length <= 1" class="absolute right-1 top-1 p-2 text-gray-400 disabled:hidden">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="p-icon p-button-icon" aria-hidden="true">
            <path
              d="M8.01186 7.00933L12.27 2.75116C12.341 2.68501 12.398 2.60524 12.4375 2.51661C12.4769 2.42798 12.4982 2.3323 12.4999 2.23529C12.5016 2.13827 12.4838 2.0419 12.4474 1.95194C12.4111 1.86197 12.357 1.78024 12.2884 1.71163C12.2198 1.64302 12.138 1.58893 12.0481 1.55259C11.9581 1.51625 11.8617 1.4984 11.7647 1.50011C11.6677 1.50182 11.572 1.52306 11.4834 1.56255C11.3948 1.60204 11.315 1.65898 11.2488 1.72997L6.99067 5.98814L2.7325 1.72997C2.59553 1.60234 2.41437 1.53286 2.22718 1.53616C2.03999 1.53946 1.8614 1.61529 1.72901 1.74767C1.59663 1.88006 1.5208 2.05865 1.5175 2.24584C1.5142 2.43303 1.58368 2.61419 1.71131 2.75116L5.96948 7.00933L1.71131 11.2675C1.576 11.403 1.5 11.5866 1.5 11.7781C1.5 11.9696 1.576 12.1532 1.71131 12.2887C1.84679 12.424 2.03043 12.5 2.2219 12.5C2.41338 12.5 2.59702 12.424 2.7325 12.2887L6.99067 8.03052L11.2488 12.2887C11.3843 12.424 11.568 12.5 11.7594 12.5C11.9509 12.5 12.1346 12.424 12.27 12.2887C12.4053 12.1532 12.4813 11.9696 12.4813 11.7781C12.4813 11.5866 12.4053 11.403 12.27 11.2675L8.01186 7.00933Z"
              fill="currentColor"
            ></path>
          </svg>
        </button>
      </div>
      <div class="flex flex-col flex-wrap md:flex-row md:gap-8 md:px-6">
        <div class="w-full md:w-1/2">
          <div class="adults-section mb-4 flex items-center justify-between">
            <div class="text-sm font-semibold md:text-lg">{{ t(`${CULTURE}.occupancy.adults`) }}</div>
            <div class="flex items-center gap-2">
              <button
                type="button"
                @click="updateAdults(index, -1)"
                :disabled="room.adults <= 1"
                class="size-8 rounded border border-[var(--p-text-color)] font-bold disabled:border-gray-300 disabled:text-gray-200 md:size-6"
              >
                <div class="flex h-full items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="4" stroke="currentColor" class="size-3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14" />
                  </svg>
                </div>
              </button>
              <div class="border-secondary-gray flex size-8 items-center justify-center border bg-white text-center text-lg font-semibold">
                {{ room.adults }}
              </div>
              <button
                type="button"
                @click="updateAdults(index, 1)"
                class="size-8 rounded border border-[var(--p-text-color)] font-bold disabled:border-gray-300 disabled:text-gray-200 md:size-6"
              >
                <div class="flex h-full items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="4" stroke="currentColor" class="size-3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </div>
              </button>
            </div>
          </div>

          <div class="children-section flex items-center justify-between">
            <div class="text-sm font-semibold md:text-lg">{{ t(`${CULTURE}.occupancy.children`) }}</div>
            <div class="flex items-center gap-2">
              <button
                type="button"
                @click="updateChildren(index, -1)"
                :disabled="room.children <= 0"
                class="size-8 rounded border border-[var(--p-text-color)] font-bold disabled:border-gray-300 disabled:text-gray-200 md:size-6"
              >
                <div class="flex h-full items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="4" stroke="currentColor" class="size-3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14" />
                  </svg>
                </div>
              </button>
              <div class="border-secondary-gray flex size-8 items-center justify-center border bg-white text-center text-lg font-semibold">
                {{ room.children }}
              </div>
              <button
                type="button"
                @click="updateChildren(index, 1)"
                :disabled="room.children >= 10"
                class="size-8 rounded border border-[var(--p-text-color)] font-bold disabled:border-gray-300 disabled:text-gray-200 md:size-6"
              >
                <div class="flex h-full items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="4" stroke="currentColor" class="size-3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </div>
              </button>
            </div>
          </div>

          <div v-if="room.children > 0" class="children-age-section mt-4 max-w-full">
            <span class="text-sm font-semibold">{{ t(`${CULTURE}.occupancy.age`) }}</span>
            <div class="mt-2 flex flex-wrap">
              <div v-for="(child, childIndex) in room.children" :key="childIndex" class="child-age m-1">
                <select v-model="room.childrenAges[childIndex]" class="border-secondary-gray rounded border p-1.5">
                  <option v-for="age in availableAges" :key="age" :value="age">
                    {{ age }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-10 flex justify-end text-[80%] md:mt-0" :class="addRoomClass">
        <button type="button" @click="addRoom" v-if="index === rooms.length - 1">
          {{ t(`${CULTURE}.occupancy.addRoom`) }}
        </button>
      </div>
    </div>
  </div>
</template>
